import { useEffect, useState } from 'react';
import { Lightbulb, X } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../hooks/useToast';
import { ApiError, apiSend } from '../lib/api';

type Props = {
  open: boolean;
  onClose: () => void;
};

const messageFromError = (err: unknown): string => {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined;
    return body?.error ?? `request failed (${err.status})`;
  }
  return err instanceof Error ? err.message : 'request failed';
};

const inputCls =
  'w-full rounded-md border border-line bg-bg-primary px-3 py-1.5 text-sm outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/30';

export const SuggestAppModal = ({ open, onClose }: Props) => {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setUrl('');
      setDescription('');
      setCategory('');
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await apiSend<{ alreadyPending: boolean }>(
        'POST',
        '/api/apps/suggest',
        {
          name: name.trim(),
          url: url.trim(),
          description: description.trim() || undefined,
          category: category.trim() || undefined,
          reason: reason.trim() || undefined,
        },
        token,
      );
      toast({
        kind: 'success',
        message: res.alreadyPending
          ? `Your suggestion for ${name} is already pending review.`
          : `Suggestion sent! An admin will review and add it if approved.`,
      });
      onClose();
    } catch (err) {
      setError(messageFromError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/30 p-4 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Suggest an app"
    >
      <div
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-line bg-bg-primary shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-line px-6 py-4">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-accent-pale text-accent">
              <Lightbulb className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-lg font-semibold leading-tight">Suggest an app</h2>
              <p className="mt-0.5 text-sm text-text-secondary">
                Tell us about an app that should be in OneDesk. Admins will review.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-9 w-9 shrink-0 place-items-center rounded text-text-tertiary hover:bg-bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
            <label className="block">
              <span className="text-xs font-medium text-text-secondary">
                App name <span className="text-status-critical">*</span>
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={100}
                className={inputCls}
                placeholder="e.g. Asana"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-text-secondary">
                URL <span className="text-status-critical">*</span>
              </span>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                maxLength={500}
                className={inputCls}
                placeholder="https://..."
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-text-secondary">Category</span>
              <input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                maxLength={50}
                className={inputCls}
                placeholder="e.g. Project management"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-text-secondary">Description</span>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                className={inputCls}
                placeholder="One-line summary of what it does"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-text-secondary">
                Why do you need this?
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                className={inputCls}
                placeholder="Helps admins understand the use case."
              />
            </label>
            {error && (
              <div className="rounded-md bg-status-critical/10 p-2 text-xs text-status-critical">
                {error}
              </div>
            )}
          </div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-line bg-bg-primary px-6 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line px-3 py-1.5 text-sm hover:bg-bg-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim() || !url.trim()}
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-contrast hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Sending...' : 'Submit suggestion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
